import {
    CheckoutLifecycleEventPostMessageName,
    CheckoutParentLifecycleEventPostMessageName,
    checkoutPostMessage,
} from '@eventbrite/checkout-postmessage-api';
import { Button } from '@eventbrite/eds-button';
import { formValidate } from '@eventbrite/eds-utils';
import { HAS_WINDOW_LISTENERS } from '@eventbrite/feature-detection';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { FooterError } from '../../components/FooterError';
import { FullPageLoadingLayer } from '../../components/fullPageLoadingLayer/FullPageLoadingLayer';
import { TICKETS_PAGE_FORM_NAME } from '../../constants';
import { DataHeapId } from '../../constants/heap';
import {
    ExpressOnlyCheckoutExperimentVariant,
    STATSIG_EVENT_NAMES,
} from '../../constants/statsig';
import { getCtaTextIncludingTotalCost } from '../../containers/ticketSelection/utils/getCtaTextIncludingTotalCost';
import { Ticket, TicketId } from '../../models/tickets';
import { getExpressOnlyCheckoutExperiment } from '../../utils/statsig';
import styles from './OnlyCheckoutPage.module.scss';
import validate from './validation';

const ConnectedExpressCheckout = loadable(
    () => import('../../components/ExpressCheckout/ConnectedExpressCheckout'),
);

export interface OnlyCheckoutPageProps {
    areExpressCheckoutConditionsReady: boolean;
    areTicketsSelected: boolean;
    canMakePaymentsWithActiveCard?: boolean;
    handleSubmit: (
        callback: (values: Record<string, unknown>) => void,
    ) => () => void;
    isAppRebrandActive: boolean;
    isApplePayElegible: boolean;
    isDisabled: boolean;
    isExpressApplePayEnabled: boolean;
    isFreeEvent: boolean;
    isMakingApplePayPayment: boolean;
    onPageWillMount: () => void;
    onSetTicketsMessageReceived: (
        selectedTickets: Record<TicketId, { selectedQuantity: number }>,
    ) => void;
    onSubmit: (values: Record<string, unknown>) => void;
    onTicketSelectionLayoutStabilized: (height: number) => void;
    selectedTickets: Record<TicketId, Ticket>;
    templateProps: {
        isSubmitting: boolean;
    };
    totalCostDisplay: string;
    validationErrorMessage?: GenericLazyString | string;
    widgetParentUrl?: string;
}

export const OnlyCheckoutPage: FunctionComponent<OnlyCheckoutPageProps> = ({
    areExpressCheckoutConditionsReady,
    areTicketsSelected,
    canMakePaymentsWithActiveCard,
    handleSubmit,
    isAppRebrandActive,
    isApplePayElegible,
    isDisabled,
    isExpressApplePayEnabled,
    isFreeEvent,
    isMakingApplePayPayment,
    onPageWillMount,
    onSetTicketsMessageReceived,
    onSubmit,
    onTicketSelectionLayoutStabilized,
    selectedTickets,
    templateProps,
    totalCostDisplay,
    validationErrorMessage,
    widgetParentUrl,
}) => {
    const pageWrapperRef = useRef<HTMLDivElement>(null);
    const resizeObserver = useRef<ResizeObserver>();

    const [hasSubmittedForm, setHasSubmittedForm] = useState<boolean>(false);
    const [pageHeight, setPageHeight] = useState<number | null>(null);
    const [hasCalledBack, setHasCalledBack] = useState<boolean>(false);
    const [isExpressCheckoutStabilized, setIsExpressCheckoutStabilized] =
        useState<boolean>(false);

    const shouldRenderExpressCheckoutButtons =
        getExpressOnlyCheckoutExperiment() ===
            ExpressOnlyCheckoutExperimentVariant.enabled &&
        isExpressApplePayEnabled &&
        isApplePayElegible;

    useEffect(() => {
        if (!HAS_WINDOW_LISTENERS) {
            return;
        }

        const handlePostMessage = (event: MessageEvent) => {
            const data = event.data;
            switch (data.type) {
                case CheckoutParentLifecycleEventPostMessageName.setTickets:
                    onSetTicketsMessageReceived(data.data);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('message', handlePostMessage);

        return () => {
            if (!HAS_WINDOW_LISTENERS) {
                return;
            }
            window.removeEventListener('message', handlePostMessage);
        };
    }, [onSetTicketsMessageReceived]);

    useEffect(() => {
        const updatePageHeight = (currentPageHeight: number) => {
            if (pageHeight === currentPageHeight) {
                return;
            }

            setPageHeight(currentPageHeight);

            const message = {
                messageName:
                    CheckoutLifecycleEventPostMessageName.ticketSelectionResize,
                additionalData: { contentHeight: currentPageHeight },
                widgetParentUrl,
            };

            checkoutPostMessage(message, { sendDictionaryToiOS: true });

            if (hasCalledBack) {
                return;
            }

            if (
                shouldRenderExpressCheckoutButtons &&
                (!areExpressCheckoutConditionsReady ||
                    !isExpressCheckoutStabilized)
            ) {
                return;
            }

            onTicketSelectionLayoutStabilized(currentPageHeight);
            setHasCalledBack(true);
        };

        resizeObserver.current = new ResizeObserver((entries) =>
            updatePageHeight(entries[0].target.clientHeight),
        );

        if (pageWrapperRef.current) {
            resizeObserver.current.observe(pageWrapperRef.current);
        }

        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
            }
        };
    }, [
        areExpressCheckoutConditionsReady,
        hasCalledBack,
        isExpressCheckoutStabilized,
        onTicketSelectionLayoutStabilized,
        pageHeight,
        shouldRenderExpressCheckoutButtons,
        widgetParentUrl,
    ]);

    useEffect(() => {
        onPageWillMount();

        if (widgetParentUrl) {
            checkoutPostMessage({
                messageName:
                    CheckoutLifecycleEventPostMessageName.onlyCheckoutLoaded,
                widgetParentUrl,
            });
        }
    }, [onPageWillMount, widgetParentUrl]);

    const _onSubmit = (data: { statsigValue?: string | number | null }) => {
        logEvent(
            STATSIG_EVENT_NAMES.TICKET_STEP_CONTINUE_TO_PAYMENT_CLICK,
            data.statsigValue,
            {
                isOnlyCheckoutFlow: 'true',
            },
        );

        if (!hasSubmittedForm && validationErrorMessage) {
            logEvent(STATSIG_EVENT_NAMES.TICKET_STEP_VALIDATION_ERROR_VIEW);
        }

        setHasSubmittedForm(true);

        if (validationErrorMessage) {
            return;
        }

        handleSubmit((values) =>
            onSubmit({
                ...values,
                ...data,
            }),
        )();
    };

    const fullPageLoadingLayer = isExpressApplePayEnabled ? (
        <FullPageLoadingLayer
            isAppRebrandActive={isAppRebrandActive}
            isShown={templateProps.isSubmitting && isMakingApplePayPayment}
        >
            <div
                className="eds-text-bl eds-mar-top-5"
                data-testid="only-checkout-loading-text"
            >
                {canMakePaymentsWithActiveCard ? gettext('Placing order') : ''}
            </div>
        </FullPageLoadingLayer>
    ) : null;

    const ctaText = getCtaTextIncludingTotalCost({
        isFreeEvent,
        areTicketsSelected,
        totalCostDisplay,
        selectedTickets,
    });

    const footerMessageNode =
        hasSubmittedForm && validationErrorMessage ? (
            <div className="eds-l-mar-bot-4">
                <FooterError className={styles.onlyCheckoutError}>
                    {validationErrorMessage}
                </FooterError>
            </div>
        ) : null;

    const checkoutButtons = shouldRenderExpressCheckoutButtons ? (
        <ConnectedExpressCheckout
            handleSubmit={_onSubmit}
            isSubmitting={templateProps.isSubmitting}
            onFullyLoaded={() => {
                setIsExpressCheckoutStabilized(true);
            }}
        />
    ) : (
        <Button
            onClick={_onSubmit}
            style="fill"
            disabled={isDisabled}
            isLoading={templateProps.isSubmitting}
            data-testid="only-checkout-button"
            data-heap-id={DataHeapId.multiticketCTAClick}
            size="block"
        >
            {ctaText}
        </Button>
    );

    return (
        <>
            {fullPageLoadingLayer}
            <section
                className="eds-l-pad-all-4 only-checkout"
                data-testid="only-checkout"
                ref={pageWrapperRef}
            >
                <div>
                    <form onSubmit={_onSubmit as any} />
                    {footerMessageNode}
                    {checkoutButtons}
                </div>
            </section>
        </>
    );
};

export default formValidate({
    form: TICKETS_PAGE_FORM_NAME,
    validate,
})(OnlyCheckoutPage);
